import { useState } from 'react'
import styled, { css } from 'styled-components'
import { IMAGE_STYLE_OPTIONS } from './styleOptions'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'
export type { StyleOption } from './styleOptions'

interface StyleCardProps {
  $selected: boolean
}

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyleCard = styled.button<StyleCardProps>`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
  background: var(--color-bg-card);
  border: 1.5px solid
    ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border-card)')};

  ${({ $selected }) =>
    $selected &&
    css`
      background: var(--color-primary-bg);
    `}

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`

const StyleName = styled.span<StyleCardProps>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-text-primary)')};
`

const DefaultTag = styled.span`
  margin-left: auto;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-primary);
  background: var(--color-primary-bg);
  border: 1px solid var(--color-primary);
  border-radius: 4px;
  padding: 2px 8px;
  line-height: 1.5;
  white-space: nowrap;
  flex-shrink: 0;
`

const StyleDesc = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

const SetDefaultButton = styled.button`
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-secondary);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.6;
  transition:
    opacity 0.12s ease,
    color 0.12s ease;

  &:hover {
    opacity: 1;
    color: var(--color-primary);
  }
`

interface StyleSelectorProps {
  value: string
  onChange: (id: string) => void
  disabled?: boolean
  /** ID of the current global default style — shows a "Default" tag */
  defaultStyleId?: string
  /** Called when user clicks "Set as default" on a non-default style */
  onSetAsDefault?: (id: string) => void
}

export function StyleSelector({
  value,
  onChange,
  disabled,
  defaultStyleId,
  onSetAsDefault,
}: StyleSelectorProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <Grid>
      {IMAGE_STYLE_OPTIONS.map((style) => {
        const selected = value === style.id
        const isDefault = style.id === defaultStyleId
        const showSetDefault = onSetAsDefault && !isDefault && hoveredId === style.id

        return (
          <StyleCard
            key={style.id}
            $selected={selected}
            onClick={() => !disabled && onChange(style.id)}
            onMouseEnter={() => setHoveredId(style.id)}
            onMouseLeave={() => setHoveredId(null)}
            disabled={disabled}
            type="button"
            aria-pressed={selected}
          >
            <TextBlock>
              <NameRow>
                <StyleName $selected={selected}>{style.name}</StyleName>
                {isDefault && <DefaultTag>Default</DefaultTag>}
                {showSetDefault && (
                  <SetDefaultButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onSetAsDefault(style.id)
                    }}
                  >
                    Set as default
                  </SetDefaultButton>
                )}
              </NameRow>
              <StyleDesc>{style.description}</StyleDesc>
            </TextBlock>
          </StyleCard>
        )
      })}
    </Grid>
  )
}
